/* GASSISA
=========================================================================================
* Descripción: Selector aditivo de empresa/BD para login multi-tenant + botón solicitar.
*              Si hay 0-1 perfiles no altera el flujo actual.
* Historial:
*      1 | 08/10/2026 | LIC. José Luis Aragón C. | Creación multi-tenant aditiva
=========================================================================================
*/

import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import { useSesion } from "../../context/System/SesionContext";
import RequestAdditionalAccessDialog from "./RequestAdditionalAccessDialog";

export default function TenantProfileSelector() {
  const {
    tenantProfiles,
    refreshTenantProfiles,
    fetchTenantProfilesFromBackend,
    syncActiveDeviceToProfiles,
    switchTenantProfile,
    tenantAdminStatus,
    tenantGateMessage,
  } = useSesion();
  const [openRequest, setOpenRequest] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchMsg, setSwitchMsg] = useState("");

  useEffect(() => {
    // Fuente de verdad: backend Seguridad (fetch list-by-hardware con mismos datos PrinterTools).
    syncActiveDeviceToProfiles?.()?.finally?.(() => {});
    fetchTenantProfilesFromBackend?.();
  }, []);

  const profiles = Array.isArray(tenantProfiles) ? tenantProfiles : [];
  let storedKey = "";
  try {
    storedKey = String(JSON.parse(localStorage.getItem("arsync-tenant-device") ?? "null")?.tenantKey ?? "").toUpperCase();
  } catch {
    storedKey = "";
  }
  const activeKey = String(tenantAdminStatus?.tenantKey ?? "").toUpperCase() || storedKey;

  const handleChange = async (e) => {
    const nextKey = String(e.target.value ?? "").toUpperCase();
    if (!nextKey || nextKey === activeKey) return;
    setSwitchMsg("");
    setSwitching(true);
    const result = await switchTenantProfile?.(nextKey);
    setSwitching(false);
    if (!result?.ok) {
      setSwitchMsg(result?.message ?? "No fue posible cambiar de empresa.");
      return;
    }
    window.location.reload();
  };

  const labelOf = (p) =>
    String(p?.tenantName || p?.databaseName || p?.tenantKey || "Empresa").trim();

  return (
    <Box sx={{ mb: 1 }}>
      <FormControl fullWidth size="small" disabled={switching}>
        <InputLabel id="tenant-profile-label">Empresa / Base de datos</InputLabel>
        <Select
          labelId="tenant-profile-label"
          label="Empresa / Base de datos"
          value={profiles.some((p) => String(p.tenantKey).toUpperCase() === activeKey) ? activeKey : ""}
          onChange={handleChange}
          displayEmpty
        >
          {profiles.length === 0 && (
            <MenuItem value="">
              <em>Este equipo tiene 1 licencia activa</em>
            </MenuItem>
          )}
          {profiles.map((p) => (
            <MenuItem key={p.tenantKey} value={String(p.tenantKey).toUpperCase()}>
              {labelOf(p)} — {p.databaseName || p.licenseCode}
              {String(p.status ?? "").toUpperCase() === "PENDING_APPROVAL" ? " (pendiente)" : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        size="small"
        startIcon={<AddLinkIcon />}
        onClick={() => setOpenRequest(true)}
        sx={{ mt: 1 }}
        fullWidth
        variant="outlined"
      >
        Solicitar acceso a otra licencia
      </Button>
      {(switchMsg || tenantGateMessage) && profiles.length > 1 && (
        <Typography variant="caption" color={switchMsg ? "warning.main" : "text.secondary"} sx={{ display: "block", mt: 1 }}>
          {switchMsg || ""}
        </Typography>
      )}
      {switchMsg && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {switchMsg}
        </Alert>
      )}
      <RequestAdditionalAccessDialog
        open={openRequest}
        onClose={() => setOpenRequest(false)}
        onDone={() => {
          refreshTenantProfiles?.();
        }}
      />
    </Box>
  );
}
